import React, { useMemo, useState } from "react";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import { API_ORIGIN } from "../services/api";

const HODDetails = ({
  data,
  history = [],
  onChange,
  currentUser,
  role,
  primaryHodId,
  disabled = false,
  showRootCauseEditor = false,
  onResend,
  assignedHods = []
}) => {
  const [preview, setPreview] = useState(null);

  const isQuality =
    role === "Admin" ||
    role === "Quality Manager" ||
    role === "Quality Staff";

  const BASE_URL = API_ORIGIN;

  const getId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value._id || value.id || "";
  };

  const getName = (value, fallback = "HOD") => {
    if (!value || typeof value === "string") return fallback;
    return value.username || value.name || fallback;
  };

  const getCommentOwnerId = (comment) =>
    getId(comment.targetHodId || comment.hodId || comment.createdBy);

  const getCommentOwnerName = (comment, fallback = "HOD") =>
    getName(comment.targetHodId || comment.hodId || comment.createdBy, fallback);

  const getAuthorLabel = (comment, isResent) => {
    if (isQuality) {
      if (isResent) return `Resent by ${getName(comment.createdBy, "Quality")}`;
      return getName(comment.hodId || comment.createdBy, "HOD");
    }

    if (isResent) {
      return getName(comment.createdBy, "Quality");
    }

    const authorId = getId(comment.hodId || comment.createdBy);
    if (authorId === currentUser) {
      return "You";
    }

    return getCommentOwnerName(comment);
  };

  const isPrimary = currentUser === getId(primaryHodId);
  const canEnterRootCause = isPrimary || showRootCauseEditor;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes("rootCause")) {
      const key = name.split(".")[1];

      onChange({
        ...data,
        rootCause: { ...data.rootCause, [key]: value }
      });
      return;
    }

    onChange({ ...data, [name]: value });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const formatted = files.map((file) => ({
      fileName: file.name,
      fileObj: file
    }));

    onChange({
      ...data,
      attachments: [...(data.attachments || []), ...formatted]
    });
  };

  const removeFile = (index) => {
    const updated = [...(data.attachments || [])];
    updated.splice(index, 1);
    onChange({ ...data, attachments: updated });
  };

  // Assigned HODs see all HOD threads on this incident (edit is gated by `disabled` in parent).
  const filtered = history;

  const rootComments = filtered.filter((comment) => !comment.parentId);
  const repliesByParent = useMemo(() => {
    return filtered.reduce((grouped, comment) => {
      const parentId = getId(comment.parentId);
      if (!parentId) return grouped;
      return {
        ...grouped,
        [parentId]: [...(grouped[parentId] || []), comment]
      };
    }, {});
  }, [filtered]);

  const latestReplyByHod = useMemo(() => {
    const latest = {};
    filtered
      .filter((comment) => comment.status !== "Resent")
      .forEach((comment) => {
        const hodId = getId(comment.hodId || comment.createdBy);
        const createdTime = new Date(comment.createdDate).getTime();
        if (!latest[hodId] || createdTime > latest[hodId].time) {
          latest[hodId] = { id: getId(comment._id), time: createdTime };
        }
      });
    return latest;
  }, [filtered]);

  const latestResendByHod = useMemo(() => {
    const latest = {};
    filtered
      .filter((comment) => comment.status === "Resent")
      .forEach((comment) => {
        const hodId = getId(comment.targetHodId || comment.hodId);
        const createdTime = new Date(comment.createdDate).getTime();
        if (!latest[hodId] || createdTime > latest[hodId]) {
          latest[hodId] = createdTime;
        }
      });
    return latest;
  }, [filtered]);

  const groupedThreads = (() => {
    const groups = {};

    assignedHods.forEach((hod) => {
      const id = getId(hod);
      if (!id) return;
      groups[id] = {
        id,
        name: getName(hod, "HOD"),
        rootComments: []
      };
    });

    rootComments.forEach((comment) => {
      const hodId = getCommentOwnerId(comment);
      if (!hodId) return;
      groups[hodId] = groups[hodId] || {
        id: hodId,
        name: getCommentOwnerName(comment),
        rootComments: []
      };
      groups[hodId].rootComments.push(comment);
    });

    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const getGroupStatus = (hodId) => {
    const latestReply = latestReplyByHod[hodId];
    const latestResend = latestResendByHod[hodId];

    if (latestResend && (!latestReply || latestResend > latestReply.time)) {
      return { label: "Waiting for reply", className: "bg-warning text-dark" };
    }

    if (latestReply) {
      return { label: "Replied", className: "bg-success" };
    }

    return { label: "Pending", className: "bg-secondary" };
  };

  const getPreviewFiles = (attachments = []) =>
    attachments.map((file) => ({
      fileName: file.fileName,
      url: `${BASE_URL}${file.fileUrl}`
    }));

  const renderAttachments = (attachments = []) => {
    if (!attachments.length) return null;
    const previewFiles = getPreviewFiles(attachments);

    return (
      <div className="comment-attachments">
        {attachments.map((file, index) => (
          <button
            key={`${file.fileUrl || file.fileName}-${index}`}
            className="btn btn-outline-secondary btn-sm"
            type="button"
            onClick={() => setPreview({ files: previewFiles, index })}
          >
            <i className="bi bi-paperclip" aria-hidden="true"></i>
            <span>{file.fileName}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderCommentNode = (comment, depth = 0) => {
    const commentId = getId(comment._id);
    const isRoot = depth === 0;
    const isResent = comment.status === "Resent";
    const hodId = getCommentOwnerId(comment);
    const latestHodReply = latestReplyByHod[hodId];
    const hasPendingResend =
      latestResendByHod[hodId] &&
      (!latestHodReply || latestResendByHod[hodId] > latestHodReply.time);
    const canResend = Boolean(onResend) && isQuality && !isResent && latestHodReply?.id === commentId && !hasPendingResend;
    const replies = repliesByParent[commentId] || [];

    return (
      <div
        key={commentId}
        className={`${isRoot ? "comment-card" : "comment-reply"} ${isResent ? "resent" : ""}`}
      >
        <div className="comment-meta">
          <strong>{getAuthorLabel(comment, isResent)}</strong>
          <span>{comment.createdDate ? new Date(comment.createdDate).toLocaleString() : ""}</span>
        </div>

        {isResent && <span className="badge bg-warning text-dark mb-2">Resent</span>}

        <div className="comment-body">{comment.hodComments || comment.remarkFromQuality}</div>

        {comment.remarkFromQuality && comment.hodComments !== comment.remarkFromQuality && (
          <div className="alert alert-warning py-2 mb-2">
            Remark: {comment.remarkFromQuality}
          </div>
        )}

        {comment.rootCause && Object.values(comment.rootCause).some(Boolean) && (
          <div className="border rounded p-2 mt-2 small">
            <strong>Root Cause</strong>
            <div>Human: {comment.rootCause.human || "-"}</div>
            <div>Material: {comment.rootCause.material || "-"}</div>
            <div>Environment: {comment.rootCause.environment || "-"}</div>
            <div>Policies: {comment.rootCause.policies || "-"}</div>
          </div>
        )}

        {renderAttachments(comment.attachments)}

        {canResend && (
          <button
            className="btn btn-sm btn-warning mt-2 resend-action-btn"
            type="button"
            onClick={() =>
              onResend({
                type: "hod",
                targetId: hodId,
                parentId: commentId
              })
            }
          >
            <i className="bi bi-arrow-repeat" aria-hidden="true"></i>
            <span>Resend Comment</span>
          </button>
        )}

        {replies.map((reply) => renderCommentNode(reply, depth + 1))}
      </div>
    );
  };

  return (
    <div className="form-container compact">
      <div className="comment-group-list">
        {groupedThreads.length === 0 && (
          <div className="text-muted small">No HOD comments yet.</div>
        )}

        {groupedThreads.map((group) => {
          const groupStatus = getGroupStatus(group.id);
          const isPrimaryHodGroup = group.id === getId(primaryHodId);

          return (
            <div key={group.id} className="comment-group">
              <div className="comment-group-header">
                <div>
                  <div className="comment-group-title">
                    {group.name}
                    {isPrimaryHodGroup && <span className="badge bg-primary ms-2">Primary</span>}
                  </div>
                  <div className="comment-group-subtitle">HOD investigation thread</div>
                </div>
                <span className={`badge ${groupStatus.className}`}>{groupStatus.label}</span>
              </div>

              <div className="comment-timeline">
                {group.rootComments.length === 0 ? (
                  <div className="text-muted small">No reply received yet.</div>
                ) : (
                  group.rootComments.map((parent) => renderCommentNode(parent))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!disabled && (
        <div className="border rounded p-3 mt-3">
          <div className="mb-3">
            <label className="fw-bold">HOD Comment</label>
            <textarea
              name="hodComments"
              value={data.hodComments || ""}
              onChange={handleChange}
              className="form-control"
              rows={4}
            />
          </div>

          <label className="upload-box">
            <i className="bi bi-paperclip" aria-hidden="true"></i>
            <span>Attach files</span>
            <input type="file" multiple onChange={handleFileChange} />
          </label>

          {data.attachments?.map((file, index) => (
            <div key={`${file.fileName}-${index}`} className="upload-chip">
              <span>{file.fileName}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                title="Remove file"
                aria-label={`Remove ${file.fileName}`}
              >
                <i className="bi bi-trash" aria-hidden="true"></i>
              </button>
            </div>
          ))}

          {canEnterRootCause && (
            <div className="border rounded p-3 mt-3">
              <h6>Root Cause</h6>
              <textarea className="form-control mb-2" name="rootCause.human" value={data.rootCause?.human || ""} placeholder="Human" onChange={handleChange} />
              <textarea className="form-control mb-2" name="rootCause.material" value={data.rootCause?.material || ""} placeholder="Material" onChange={handleChange} />
              <textarea className="form-control mb-2" name="rootCause.environment" value={data.rootCause?.environment || ""} placeholder="Environment" onChange={handleChange} />
              <textarea className="form-control" name="rootCause.policies" value={data.rootCause?.policies || ""} placeholder="Policies" onChange={handleChange} />
            </div>
          )}
        </div>
      )}

      <AttachmentPreviewModal
        file={preview?.files?.[preview.index]}
        files={preview?.files || []}
        currentIndex={preview?.index || 0}
        onNavigate={(index) => setPreview((current) => ({ ...current, index }))}
        onClose={() => setPreview(null)}
      />
    </div>
  );
};

export default HODDetails;
